<template>
  <div class="customer-form-page">
    <section class="page-header fade-in">
      <div class="header-left">
        <button class="btn btn-light" @click="goBack">
          <i class="bi bi-arrow-left"></i>
          <span>Retour</span>
        </button>
        <div>
          <h1 class="page-title">Modifier le client</h1>
          <p class="page-subtitle" v-if="form.fullName">
            Mise à jour des informations pour
            <strong>{{ form.fullName }}</strong>
          </p>
        </div>
      </div>
    </section>

    <section class="fade-in" v-if="!initialLoading">
      <form class="" @submit.prevent="handleSubmit">
        <div class="">
          <div class="form-row">
            <div class="form-group">
              <label>Nom complet</label>
              <input
                v-model.trim="form.fullName"
                type="text"
                class="form-control"
                :class="{ 'has-error': errors.fullName }"
                placeholder="Ex: Jean Dupont"
              />
              <p v-if="errors.fullName" class="error-text">
                {{ errors.fullName }}
              </p>
            </div>

            <div class="form-group">
              <label>Téléphone *</label>
              <input
                v-model.trim="form.phone"
                type="tel"
                class="form-control"
                :class="{ 'has-error': errors.phone }"
                placeholder="Ex: +212 6 00 00 00 00"
              />
              <p v-if="errors.phone" class="error-text">{{ errors.phone }}</p>
            </div>

            <div class="form-group">
              <label>Société</label>
              <input
                v-model.trim="form.society"
                type="text"
                class="form-control"
                placeholder="Ex: Peascom Caisse Manager"
              />
            </div>

            <div class="form-group">
              <label>Plafond impayé</label>
              <input
                v-model="form.maxUnpaid"
                type="number"
                min="0"
                step="0.01"
                class="form-control"
                :class="{ 'has-error': errors.maxUnpaid }"
                placeholder="0.00"
              />
              <p v-if="errors.maxUnpaid" class="error-text">
                {{ errors.maxUnpaid }}
              </p>
            </div>
          </div>

          <div class="form-group">
            <label>Adresse</label>
            <textarea
              v-model.trim="form.address"
              rows="3"
              class="form-control"
              placeholder="Adresse complète"
            ></textarea>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Ville</label>
              <input
                v-model.trim="form.city"
                type="text"
                class="form-control"
                placeholder="Ville"
              />
            </div>
          </div>

          <div class="form-group">
            <label>Remarque</label>
            <textarea
              v-model.trim="form.remark"
              rows="2"
              class="form-control"
              placeholder="Notes internes"
            ></textarea>
          </div>
        </div>

        <div class="form-actions">
          <button
            type="button"
            class="btn btn-light"
            @click="goBack"
            :disabled="submitting"
          >
            Annuler
          </button>
          <button type="submit" class="btn btn-primary" :disabled="submitting">
            <i class="bi bi-save"></i>
            <span v-if="!submitting">Mettre à jour</span>
            <span v-else>Mise à jour...</span>
          </button>
        </div>
      </form>
    </section>

    <div v-else class="loading-state">
      <div class="spinner"></div>
      <p>Chargement du client...</p>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "CustomerEdit",
  data() {
    return {
      initialLoading: true,
      submitting: false,
      errors: {},
      form: {
        reference: "",
        fullName: "",
        phone: "",
        society: "",
        maxUnpaid: "",
        address: "",
        city: "",
        remark: "",
      },
      fullNameTouched: false,
    };
  },
  computed: {
    ...mapGetters("customer", {
      customer: "getCustomer",
    }),
    customerReference() {
      return this.$route.params.reference;
    },
  },
  watch: {
    customer: {
      handler(newValue) {
        console.log("Customer watch triggered:", newValue);
        if (!newValue || Object.keys(newValue).length === 0) {
          // If customer is empty, make sure loading stops
          if (this.initialLoading) {
            this.initialLoading = false;
          }
          return;
        }
        this.populateForm(newValue);
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    goBack() {
      this.$router.back();
    },
    populateForm(customerData) {
      console.log("Populating form with:", customerData);
      const mapped = this.mapCustomerToForm(customerData);
      console.log("Mapped data:", mapped);

      // Use Vue.set or direct assignment for reactivity
      Object.keys(mapped).forEach((key) => {
        this.$set(this.form, key, mapped[key]);
      });

      this.initialLoading = false;
    },
    mapCustomerToForm(customer) {
      const toNumber = (value) => {
        if (value === "" || value === null || value === undefined) {
          return "";
        }
        const numeric = Number(value);
        return Number.isNaN(numeric) ? "" : String(numeric);
      };

      const toString = (value) => {
        return value === null || value === undefined ? "" : String(value);
      };

      return {
        reference: toString(customer?.reference || this.customerReference),
        fullName: toString(
          customer?.fullName != null
            ? customer?.fullName
            : customer?.full_name != null
            ? customer?.full_name
            : customer?.name
        ),
        phone: toString(customer?.phone),
        society: toString(
          customer?.society != null ? customer?.society : customer?.company
        ),
        maxUnpaid: toNumber(
          customer?.maxUnpaid != null
            ? customer?.maxUnpaid
            : customer?.max_unpaid
        ),
        address: toString(customer?.address),
        city: toString(customer?.city),
        remark: toString(customer?.remark),
      };
    },
    validate() {
      const errors = {};

      console.log("Validating form:", this.form);

      this.errors = errors;
      const isValid = Object.keys(errors).length === 0;
      console.log("Validation result:", { isValid, errors });
      return isValid;
    },
    async loadCustomer() {
      this.initialLoading = true;
      try {
        const result = await this.$store.dispatch(
          "customer/show",
          this.customerReference
        );
        console.log("Load customer result:", result);
        console.log("Customer from store after dispatch:", this.customer);

        // Wait a bit for the store to update
        await this.$nextTick();
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Ensure form is populated even if watch doesn't trigger
        if (this.customer && Object.keys(this.customer).length > 0) {
          this.populateForm(this.customer);
        } else if (result && Object.keys(result).length > 0) {
          this.populateForm(result);
        } else {
          this.initialLoading = false;
        }
      } catch (error) {
        console.error("Error loading customer", error);
        this.$store.dispatch("notification/store", {
          status: "error",
          message: "Impossible de charger le client.",
        });
        this.initialLoading = false;
      }
    },
    buildPayload() {
      const payload = {
        ...this.form,
        maxUnpaid:
          this.form.maxUnpaid === "" ||
          this.form.maxUnpaid === null ||
          this.form.maxUnpaid === undefined
            ? 0
            : Number(this.form.maxUnpaid),
      };

      if (Number.isNaN(payload.maxUnpaid)) {
        payload.maxUnpaid = 0;
      }

      return payload;
    },
    async handleSubmit() {
      console.log("Form submit triggered");
      console.log("Form data:", this.form);

      if (!this.validate()) {
        console.log("Validation failed:", this.errors);
        this.$toast?.error?.("Veuillez corriger les erreurs du formulaire");
        return;
      }

      this.submitting = true;
      try {
        const payload = this.buildPayload();
        console.log("Sending payload:", payload);
        console.log("Customer reference:", this.customerReference);

        const response = await this.$store.dispatch("customer/update", {
          reference: this.customerReference,
          data: payload,
        });

        console.log("Update response:", response);

        this.$store.dispatch("notification/store", {
          status: "success",
          message: "Client mis à jour avec succès.",
        });

        // Wait a bit before navigating to ensure notification is shown
        await new Promise((resolve) => setTimeout(resolve, 500));

        this.$router.push({
          name: "customers-show",
          params: { reference: this.customerReference },
        });
      } catch (error) {
        console.error("Error updating customer:", error);
        console.error("Error details:", {
          message: error?.message,
          response: error?.response,
          data: error?.response?.data,
        });

        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Une erreur est survenue lors de la mise à jour.";

        this.$store.dispatch("notification/store", {
          status: "error",
          message: errorMessage,
        });

        this.$toast?.error?.(errorMessage);
      } finally {
        this.submitting = false;
      }
    },
  },
  async mounted() {
    console.log("Component mounted");
    console.log("Current customer from store:", this.customer);
    console.log("Customer reference from route:", this.customerReference);

    // Always ensure customer data is loaded and mapped
    if (
      !this.customer ||
      Object.keys(this.customer).length === 0 ||
      this.customer.reference !== this.customerReference
    ) {
      await this.loadCustomer();
    } else {
      // If customer already exists in store, populate form directly
      console.log("Customer exists in store, populating form");
      this.populateForm(this.customer);
    }
  },
  beforeDestroy() {
    this.$store.dispatch("customer/reset");
  },
};
</script>

<style scoped>
.customer-form-page {
  min-height: 100vh;
  background: var(--bg-secondary);
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.header-left {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.page-title {
  font-size: 2.25rem;
  font-weight: 700;
  margin: 0;
}

.page-subtitle {
  margin: 0;
  color: var(--text-secondary);
}

.section-title {
  margin: 0;
  font-size: 1.25rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

label {
  font-weight: 600;
  color: var(--text-secondary);
}

.helper-text {
  margin: -0.35rem 0 0;
  font-size: 0.82rem;
  color: var(--text-muted, rgba(71, 85, 105, 0.85));
}

input,
textarea {
  border-radius: var(--radius-md);
  border: 1px solid var(--border-color);
  padding: 0.75rem 1rem;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.form-actions {
  grid-column: 1 / -1;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1rem;
}

.has-error {
  border-color: var(--danger-color);
}

.error-text {
  color: var(--danger-color);
  font-size: 0.875rem;
  margin: 0;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  min-height: 60vh;
  color: var(--text-secondary);
}

.spinner {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 4px solid var(--border-color);
  border-top-color: var(--primary-color);
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 1024px) {
  .form-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .customer-form-page {
    padding: 1rem;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .form-actions {
    flex-direction: column-reverse;
    align-items: stretch;
  }
}

@media (max-width: 480px) {
  .page-title {
    font-size: 1.75rem;
  }
}
</style>
